import type { ModelLead } from '@app/api/Leads/types';

import type { LeadsRegistryResponseTime } from '../types';

const SECONDS_IN_MINUTE = 60;
const SECONDS_IN_HOUR = 60 * SECONDS_IN_MINUTE;
const SECONDS_IN_DAY = 24 * SECONDS_IN_HOUR;

interface FormatOptions {
  withSeconds?: boolean;
}

const formatDurationSeconds = (
  totalSeconds: number,
  { withSeconds = true }: FormatOptions = {}
): string => {
  if (!Number.isFinite(totalSeconds)) return withSeconds ? '0 с' : '0 мин';

  const safe = Math.max(0, Math.floor(totalSeconds));
  const days = Math.floor(safe / SECONDS_IN_DAY);
  const hours = Math.floor((safe % SECONDS_IN_DAY) / SECONDS_IN_HOUR);
  const minutes = Math.floor((safe % SECONDS_IN_HOUR) / SECONDS_IN_MINUTE);
  const seconds = safe % SECONDS_IN_MINUTE;
  const parts: string[] = [];

  if (days > 0) parts.push(`${days} д`);
  if (hours > 0) parts.push(`${hours} ч`);
  if (minutes > 0) parts.push(`${minutes} мин`);
  if (withSeconds && seconds > 0) parts.push(`${seconds} с`);

  if (parts.length === 0) return withSeconds ? '0 с' : '0 мин';

  return parts.join(' ');
};

const getServerDiffSeconds = (
  laterIso: string,
  earlierIso: string
): number | null => {
  const laterMs = Date.parse(laterIso);
  const earlierMs = Date.parse(earlierIso);

  if (isNaN(laterMs) || isNaN(earlierMs)) return null;

  return Math.floor((laterMs - earlierMs) / 1000);
};

export const getLeadsRegistryResponseTime = ({
  lead,
  tickSeconds,
}: {
  lead: ModelLead;
  tickSeconds: number;
}): LeadsRegistryResponseTime | null => {
  const { AnswerTimeInMinutes, NoAnswerSince, TimeUtc } = lead;

  if (typeof AnswerTimeInMinutes === 'number') {
    return {
      status: 'answered',
      label: formatDurationSeconds(
        Math.max(0, AnswerTimeInMinutes) * SECONDS_IN_MINUTE,
        { withSeconds: false }
      ),
    };
  }

  if (NoAnswerSince && TimeUtc) {
    const initialElapsed = getServerDiffSeconds(TimeUtc, NoAnswerSince);

    if (initialElapsed !== null) {
      const elapsed =
        Math.max(0, initialElapsed) + Math.max(0, Math.floor(tickSeconds));

      return {
        status: 'overdue',
        label: formatDurationSeconds(elapsed),
      };
    }
  }

  return null;
};
